import _ from 'lodash';
import angular from 'angular';
import ComponentsModule from '../components-module';
import template from './code-workflow-inbox.html';
import Sortable from 'sortablejs';
import Debug from 'ace-ui/ace-debug';

/* global $ */

const COMPONENT_NAME = 'codeWorkflowInbox';
const debug = new Debug (COMPONENT_NAME);
const SEARCH_INTERVAL = 20000;

const UPDATE_MODE = {
  EVENT: 'event',
  SEARCH: 'search',
  OFF: 'off'
};

const VARIANT = 'aceUISearch';
const WORKFLOW_SUBSCRIPTION = 'WI_WORKFLOW';

angular.module (ComponentsModule)
  .component (COMPONENT_NAME, {
    template,

    bindings: {
      'activeMode': '<',
      'codeConfig': '<',
      'notifyUpdate': '&'
    },

    controller: function (
      $interval,
      $q,
      $rootScope,
      $scope,
      Application,
      AuthenticationService,
      ClipboardService,
      CodeWorkflow,
      ContentCardService,
      ContentEditor,
      ContentEventService,
      ContentService,
      SearchService,
      UserDataService) {

      const throttleWaitMs = 1500;

      let ctrl = this;
      let processingQueue = [];
      let userContexts = undefined;

      const resolveUpdateMode = () => {
        if (!Application.currentUser.token) {
          return UPDATE_MODE.OFF;
        }

        if (ctrl.connected) {
          return UPDATE_MODE.EVENT;
        }

        return UPDATE_MODE.SEARCH;
      };

      const entryIndexFor = (alias) => {
        return ctrl.entries.findIndex ((i) => i.id === alias);
      };

      const fetchEntry = (version, revision, event) => {
        return ContentService.getContentVersion({ version, variant: VARIANT, atLeastRevision: revision })
          .then ((response) => {
            debug.log ('fetched entry');
            return toEntry (response.content, revision, event);
          });
      };

      const contextsHasOwner = (contexts, owners) => {
        // If there are no user contexts, we always pass

        if ((contexts || []).length === 0) {
          return true;
        }

        // If there are user contexts, we only pass if at least one of
        // the contexts is matched against an owner

        return (contexts || []).filter(context => {
          return owners.indexOf(context) !== -1;
        }).length > 0;
      };

      const eventHandler = (eventData) => {
        debug.log ('Got event', eventData);
        ctrl.connected = true;
        processingQueue.push(eventData);
        updateEntryList();
      };

      const updateEntryList = _.throttle(() => {
        debug.log ('Updating entry list', processingQueue.length);

        let newEntries = [];
        let updateStatus = {
          updatedEntries: 0,
          removedEntries: 0
        };

        processingQueue.splice(-50).forEach((eventData) => {
          let mainAlias = eventData.system.mainAlias;
          let version = eventData.current.versionId;
          let revision = eventData.current.revision;
          let workflow = eventData.current.metadata.workflow;
          let isHidden = eventData.current.metadata.hidden;
          let hasNoWorkflow = !workflow;
          let configured = true;

          if (workflow && ctrl.codeConfig.workflowStates && ctrl.codeConfig.workflowStates.length) {
            configured = workflow && ctrl.codeConfig.workflowStates
              .includes(workflow.events[workflow.events.length - 1].state);
          }

          if (isHidden || hasNoWorkflow || !configured) {
            let entryIndex = entryIndexFor(mainAlias);
            let removed = ctrl.entries.splice(entryIndex, 1);

            if (removed.length > 0) {
              updateStatus.removedEntries += 1;
            }
          } else {
            let owners = eventData.current.metadata.owners || [];

            if (contextsHasOwner(userContexts, owners)) {
              newEntries.unshift(fetchEntry(version, revision));
              updateStatus.updatedEntries += 1;
            } else {
              let entryIndex = entryIndexFor(mainAlias);
              let removed = ctrl.entries.splice(entryIndex, 1);

              if (removed.length > 0) {
                updateStatus.removedEntries += 1;
              }
            }
          }
        });
        $q.all(newEntries)
          .then((entries) => {
            entries.forEach((entry) => {
              if (entryIndexFor(entry.id) >= 0) {
                ctrl.entries.splice(entryIndexFor(entry.id), 1);
              }

              ctrl.entries.unshift(entry);
            });

            ctrl.entries = ctrl.entries.splice(0, 50);
            updateStatus.updatedEntries && ctrl.notifyUpdate({ increment: updateStatus.updatedEntries });

            if (ctrl.entries.length === 0) {
              ctrl.notifyUpdate({ increment: 0 });
            }

            debug.log ('Updated entryList');
          });

        processingQueue = [];
      }, throttleWaitMs);

      function initializeUserContexts () {
        if (userContexts !== undefined) {
          return $q.when();
        }

        return UserDataService.getUserContexts()
          .then(contexts => {
            userContexts = contexts;
          })
          .catch(error => {
            console.error('Error fetching user contexts for current user. No workflow events will be processed.', error);
          });
      }

      const toEntry = (content, revision) => {
        let searchResult = ContentCardService.fromAceUISearch (content);
        let entry = {
          key: content.system.version,
          id: content.system.id,
          revision
        };

        resolveWorkflowState (entry, revision)
          .then (workflow => {
            entry.isInteresting = !!workflow;

            entry.contentCard = searchResult
              .getContextual ({ datetime: { shortDateFormat: true }, thumbnail: { aspectRatio: '1:1', width: 45 }}, 'LIST', 'CONTENT-WORKFLOW');

            ContentCardService.resolveThumbnail (entry.contentCard.thumbnail)
              .then (thumbnail => { entry.contentCard.thumbnail = thumbnail; });

            entry.contentCard.contentType.label = entry.contentCard.contentType.label || entry.contentCard.contentType.value;

            if (workflow && workflow.workflowStateLabel) {
              let workflowTag = { value: workflow.workflowStateLabel };
              if (workflow.comment) {
                workflowTag.comment = workflow.comment;
                workflowTag.commenter = workflow.commenter;
              }
              entry.contentCard.tag (workflowTag);

              if (workflow && workflow.workflowState && workflow.workflowState.invalid) {
                let title = 'The current state (' + workflow.workflowState.name + ') no longer exists in workflow definition.';
                entry.contentCard.tag({ value: '<i class="material-icons text-danger md-18" title="' + title + '">error_outline</i>' });
              }
            }
          });

        debug.log ('returning entry', entry);
        return entry;
      };

      const resolveWorkflowState = (entry, revision) => {
        let result = {};
        return $q.all([
          AuthenticationService.getCurrentUser(),
          ContentService.getContentMetadata({ alias: entry.id, atLeastRevision: revision }).then((response) => response.metadata)
        ])
          .then(([user, metadata]) => {
            debug.log ('getting contextual workflow...', [user, metadata]);
            return CodeWorkflow.getContextualWorkflow({ user, metadata, content: { system: { version: entry.key }} });
          })
          .then((workflow) => {
            if (workflow.state && (workflow.transitions.length > 0 || workflow.state.invalid)) {
              result.workflowState = workflow.state;
              result.comment = workflow.state.comment;
              result.commenter = workflow.state.submitter;
              result.workflowStateLabel = workflow.state.label || workflow.state.name;
              return result;
            } else {
              let indexOf = ctrl.entries.indexOf(entry);

              if (indexOf >= 0) {
                ctrl.entries.splice(indexOf, 1);
              }
            }
          })
          .catch((error) => {
            console.error('ERROR IN WORKFLOW INBOX RESOLVE WORKFLOW', error);
          });
      };

      const inboxMainQuery = (userContexts) => {
        let contexts = userContexts || [];
        return contexts.length > 0 ? 'ace_security_context_ss:(' + contexts.join(' ') + ')' : '*:*';
      };

      const inboxContentTypeQuery = (contentTypes) => {
        return contentTypes ? 'ace_type_s:(' + contentTypes.join(' ') + ')' : undefined;
      };

      const inboxWorkflowStateQuery = (workflowStates) => {
        let states = workflowStates || [];
        return states.length > 0 ? 'ace_workflowstate_state_s:(' + states.join(' ') + ')' : undefined;
      };

      ctrl.refreshWorkflow = () => {
        debug.log ('Refreshing workflow');

        const skipSearch = !ctrl.activeMode
        || ctrl.codeConfig.contentTypes && ctrl.codeConfig.contentTypes.length === 0
        || ctrl.codeConfig.workflowStates && ctrl.codeConfig.workflowStates.length === 0;

        if (skipSearch) {
          debug.log ('Skipping refresh.');
          return $q.resolve();
        }

        if (!ctrl.refreshing) {
          let mainQuery = inboxMainQuery(userContexts);
          let filterQueries = [inboxContentTypeQuery(ctrl.codeConfig.contentTypes),
            inboxWorkflowStateQuery(ctrl.codeConfig.workflowStates)].filter(fq => fq);

          debug.log ('Performing search.');
          debug.log ('mainQuery', mainQuery);
          debug.log ('filterQueries', filterQueries);

          ctrl.refreshing = SearchService.search({
            params: {
              q : mainQuery,
              fq : filterQueries,

              sort: 'ace_modificationTime_dt desc'
            },

            variant: 'aceUISearch'
          })
            .then((searchResponse) => {
              debug.log ('searchResponse', searchResponse);
              if (ctrl.loggedIn === false) {
                return;
              }
              ctrl.entries = searchResponse.response.docs
                .reduce ((acc, curr) => {
                  // It can contain same content with different revisions...
                  // This should not have to be done here. Smarter query? Deeper solution?
                  let existingIndex = acc.findIndex((doc) => doc.ace_mainAlias_s === curr.ace_mainAlias_s);

                  if (existingIndex === -1) {
                    acc.push(curr);
                  } else if (acc[existingIndex].aceRevision_l < curr.aceRevision_l) {
                    acc[existingIndex] = curr;
                  }

                  return acc;
                }, [])
                .map ((doc) => toEntry(doc._content, doc.aceRevision_l));

              debug.log ('Inbox refreshed.', ctrl.entries);

              ctrl.refreshing = undefined;
            })
            .catch ((error) => {
              console.error('ERROR: refreshing ContentActivityInbox.', error);
              ctrl.refreshing = undefined;
            });
        }

        return ctrl.refreshing;
      };

      ctrl.copyEntry = (event, { alias, contentType }) => {
        $(event.currentTarget).blur();

        event.stopPropagation();
        event.preventDefault();

        ClipboardService.putInClipboard([
          {
            id: alias,
            type: contentType.value
          }
        ])
          .catch ((error) => {
            console.warn('Error: Failed to insert content reference(s) into clipboard!', error);
          });
      };

      ctrl.openEntry = (entry) => {
        ContentEditor.openContent(entry.id);
      };

      ctrl.setUpdateMode = (mode) => {
        debug.log ('setUpdateMode (\'' + mode + '\')');
        if (mode === ctrl.updateMode) {
          return;
        } else if (mode === UPDATE_MODE.OFF) {
          $interval.cancel(ctrl.searchIntervalId);
          ctrl.searchIntervalId = 0;
          ContentEventService.pause(WORKFLOW_SUBSCRIPTION);
          ctrl.updateMode = UPDATE_MODE.OFF;
        } else if (mode === UPDATE_MODE.EVENT) {
          $interval.cancel(ctrl.searchIntervalId);
          ctrl.searchIntervalId = 0;
          ContentEventService.resume(WORKFLOW_SUBSCRIPTION);
          ctrl.updateMode = UPDATE_MODE.EVENT;
        } else if (mode === UPDATE_MODE.SEARCH) {
          if (!ctrl.searchIntervalId) {
            ctrl.searchIntervalId = $interval (ctrl.refreshWorkflow, SEARCH_INTERVAL);
            ctrl.updateMode = UPDATE_MODE.SEARCH;
          }
        }
      };

      ctrl.isEmpty = () => {
        let result = ctrl.entries.find (entry => entry.isInteresting === true) === undefined;
        return result;
      };

      ctrl.interestingFilter = (entry) => {
        return entry.isInteresting;
      };

      ctrl.$onInit = () => {
        ctrl.entries = [];
        ctrl.connected = false;
        ctrl.showComment = true;

        $scope.$on ('aceui:authenticationSuccess', () => {
          ctrl.setUpdateMode(resolveUpdateMode());

          if (ctrl.updateMode !== UPDATE_MODE.OFF) {
            initializeUserContexts()
              .then(() => (ctrl.refreshWorkflow()));
          }

          debug.log ('Authenticated. UPDATE_MODE.' + ctrl.updateMode.toUpperCase());
        });

        $rootScope.$on('aceui:authenticationRequest', () => {
          ctrl.setUpdateMode (UPDATE_MODE.OFF);
          debug.log ('Authentication request. UPDATE_MODE.' + ctrl.updateMode.toUpperCase());
        });

        $scope.$on ('aceui:userLoggedOut', () => {
          ctrl.entries = [];
          ctrl.setUpdateMode (UPDATE_MODE.OFF);
        });

        $scope.$watch(() => ContentEventService.connected, (newValue) => {
          debug.log ('Connection status changed', newValue);
          ctrl.connected = newValue;
          ctrl.setUpdateMode (resolveUpdateMode());
        });

        initializeUserContexts()
          .then(() => {
            ctrl.$onDestroy = ContentEventService.subscribe(WORKFLOW_SUBSCRIPTION, eventHandler);
          });

        Sortable.create($('.inbox-main-container ul')[0], {
          group: {
            name: 'content-group',
            pull: 'clone',
            put: false
          },

          sort: false,

          setData: (dt, el) => {
            dt.setData('contentId', $(el).data('contentid'));
            dt.setData('contentType', $(el).data('type'));
          }
        });
      };

      ctrl.$onChanges = ({ activeMode, codeConfig }) => {
        if (activeMode) {
          if (activeMode.currentValue) {
            debug.log ('Enter active mode');

            ctrl.setUpdateMode(resolveUpdateMode());

            if (ctrl.updateMode !== UPDATE_MODE.OFF) {
              initializeUserContexts()
                .then(() => (ctrl.refreshWorkflow()));
            }
          } else {
            debug.log ('Enter passive mode');
            ctrl.setUpdateMode (UPDATE_MODE.OFF);
            updateEntryList.cancel();
          }
        }

        if (codeConfig && codeConfig.currentValue) {
          let config = codeConfig.currentValue;
          let subscriptions = {};

          let eventTypes = config.eventTypes ? config.eventTypes : ['WORKFLOW'];

          eventTypes.forEach((eventType) => {
            subscriptions['WI_' + eventType] = {
              eventType,
              system: {
                contentType: config.contentTypes
              }
            };
          });

          ContentEventService.addSubscriptions(subscriptions);
        }
      };
    }
  });

export default COMPONENT_NAME;
